/* eslint-disable max-lines */
import { BenefitTypes } from '@strata/constants';
import { DateFormats, formatDate } from '@strata/helper-functions';
import { DateInputFormik } from '@strata/react-date-time';
import { FormContainer } from '@strata/react-form-container';
import { FormSectionWithRows } from '@strata/react-form-section';
import { DollarInputFormik, NumberInputFormik, TextInputFormik } from '@strata/react-inputs';
import { Spinner } from '@strata/react-loading-animation';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import { useEffect, useState } from 'react';
import * as Yup from 'yup';

import { postNewClaim } from 'api/oopsAPI';
import { type MemberType, MemberTypeOptions, MemberTypes } from 'services/constants';
import { handleError } from 'services/Error';
import { useLazyGetMemberListQuery, useLazyGetProductListQuery } from 'store/api/oopsApi';
import {
    type PayerSegment,
    useGetPayerSegmentListQuery,
    useGetPlaceOfServiceOptionsQuery,
    useGetSupplierListQuery,
} from 'store/api/referenceApi';

const ClaimStatuses = [
    { value: 'I', text: 'Incomplete' },
    { value: 'D', text: 'Reversed' },
];

const ValidationSchema = Yup.object({
    segment_id: Yup.string().required(validationStrings.requiredField.errorMessage),
    payer_id: Yup.string().required(validationStrings.requiredField.errorMessage),
    claim_id: Yup.string().required(validationStrings.requiredField.errorMessage),
    product_service_oid: Yup.string().required(validationStrings.requiredField.errorMessage),
    product_service_id: Yup.string().required(validationStrings.requiredField.errorMessage),
    supplier_npi: Yup.string().required(validationStrings.requiredField.errorMessage),
    claim_date_of_service: DateInputFormik.validator().required(
        validationStrings.requiredField.errorMessage,
    ),
    adjudication_date: DateInputFormik.validator().required(
        validationStrings.requiredField.errorMessage,
    ),
    place_of_service: Yup.string().required(validationStrings.requiredField.errorMessage),
    member: Yup.object({
        memberId: Yup.string().required(validationStrings.requiredField.errorMessage),
        firstName: Yup.string().required(),
        lastName: Yup.string().required(),
        dateOfBirth: Yup.string().required(),
    }).required(),
    drug_days_supply: Yup.number().required(validationStrings.requiredField.errorMessage),
    drug_quantity: Yup.number().required(validationStrings.requiredField.errorMessage),
    drug_ingredient_cost: Yup.number().required(validationStrings.requiredField.errorMessage),
    drug_prescription_number: Yup.string()
        .matches(/^\d+$/, 'Numeric characters only.')
        .required(validationStrings.requiredField.errorMessage),
    pt_deductible: Yup.number(),
    pt_coinsurance: Yup.number(),
    pt_copay: Yup.number(),
    pt_pay_other: Yup.number(),
    sequence_number: Yup.number().required(validationStrings.requiredField.errorMessage),
    claim_status: Yup.string().required(validationStrings.requiredField.errorMessage),
});

export const TestClaim = () => {
    const [generatedClaim, setGeneratedClaim] = useState<object>();

    const { data: payerSegmentList, isFetching: payerSegmentListFetching } =
        useGetPayerSegmentListQuery();
    const { data: placeOfServiceOptions, isFetching: placeOfServiceOptionsFetching } =
        useGetPlaceOfServiceOptionsQuery();
    const { data: supplierList, isFetching: supplierListFetching } = useGetSupplierListQuery();

    const [selectedSegment, setSelectedSegment] = useState<PayerSegment>();
    const [selectedMemberType, setSelectedMemberType] = useState<MemberType>(MemberTypes.TEST);

    const [getMemberList, { data: memberList, isFetching: memberListFetching }] =
        useLazyGetMemberListQuery();
    useEffect(() => {
        void (async () => {
            if (selectedSegment && selectedMemberType) {
                await getMemberList({
                    payerId: selectedSegment.payerId,
                    memberType: selectedMemberType,
                });
            }
        })();
    }, [selectedSegment, selectedMemberType, getMemberList]);

    const [getProductList, { data: productList, isFetching: productListFetching }] =
        useLazyGetProductListQuery();
    useEffect(() => {
        void (async () => {
            if (selectedSegment) {
                await getProductList({
                    segmentId: selectedSegment.segmentId,
                    benefit: BenefitTypes.PHARMACY,
                });
            }
        })();
    }, [selectedSegment, getProductList]);

    return (
        <Formik
            initialValues={
                {
                    drug_days_supply: 30,
                    drug_quantity: 28,
                    sequence_number: 1,
                    drug_prescription_number: '1',
                    claim_status: 'I',
                } as Yup.InferType<typeof ValidationSchema>
            }
            validationSchema={ValidationSchema}
            onSubmit={async (values) => {
                try {
                    const response = await postNewClaim({
                        payer_id: values.payer_id,
                        segment_id: values.segment_id,
                        claim_id: values.claim_id,
                        product_service_oid: values.product_service_oid,
                        product_service_id: values.product_service_id,
                        supplier_npi: values.supplier_npi,
                        claim_date_of_service: formatDate(
                            values.claim_date_of_service,
                            DateFormats.HYPHEN_DATE,
                        ),
                        adjudication_date: formatDate(
                            values.adjudication_date,
                            DateFormats.HYPHEN_DATE,
                        ),
                        member_id: values.member.memberId,
                        pt_first_name: values.member.firstName,
                        pt_last_name: values.member.lastName,
                        pt_dob: values.member.dateOfBirth,
                        drug_prescription_number: values.drug_prescription_number,
                        place_of_service: values.place_of_service,
                        claim_status: values.claim_status?.trim(),
                        sequence_number: values.sequence_number,
                        drug_days_supply: values.drug_days_supply,
                        drug_quantity: values.drug_quantity,
                        drug_ingredient_cost: values.drug_ingredient_cost,
                        pt_deductible: values.pt_deductible,
                        pt_coinsurance: values.pt_coinsurance,
                        pt_copay: values.pt_copay,
                        pt_pay_other: values.pt_pay_other,
                    });
                    SuccessToast({ message: 'Test Claim Created.' });
                    setGeneratedClaim(response);
                } catch (err) {
                    setGeneratedClaim({});
                    handleError('Error Creating Test Claim.', err as Error);
                }
            }}
        >
            {(formik) => (
                <FormContainer
                    formTitle="Create Test Pharmacy Claim"
                    primaryButtonLabel="Create Claim"
                    onFormSubmit={formik.handleSubmit}
                    isSubmitting={formik.isSubmitting || !(formik.dirty && formik.isValid)}
                    onCancelClick={formik.resetForm}
                    formLeftContent={
                        <>
                            <SuggestSingleSelectFormik
                                id="segment_id"
                                label="Payer Segment"
                                required
                                isLoading={payerSegmentListFetching}
                                values={payerSegmentList}
                                helperText="Payer segment to manage routes for"
                                selectedItem={payerSegmentList?.find(
                                    ({ segmentId }) => segmentId === formik.values.segment_id,
                                )}
                                onChange={(newValue) => {
                                    void (async () => {
                                        await formik.setFieldValue(
                                            'segment_id',
                                            newValue?.segmentId,
                                        );
                                        await formik.setFieldValue('payer_id', newValue?.payerId);
                                        setSelectedSegment(newValue);
                                    })();
                                }}
                            />
                            <TextInputFormik label="Claim ID" id="claim_id" required />
                            <SuggestSingleSelectFormik
                                id="product_service_oid"
                                required
                                label="Product"
                                isLoading={productListFetching}
                                values={productList}
                                selectedItem={productList?.find(
                                    ({ oid }) => oid === formik.values.product_service_oid,
                                )}
                                onChange={(newValue) => {
                                    void (async () => {
                                        await formik.setFieldValue(
                                            'product_service_oid',
                                            newValue?.oid,
                                        );
                                        await formik.setFieldValue(
                                            'product_service_id',
                                            newValue?.productCode,
                                        );
                                    })();
                                }}
                            />
                            <RadioButtonGroupFormik
                                name="memberType"
                                label="Member Type"
                                options={MemberTypeOptions}
                                selectedValue={selectedMemberType}
                                onChange={(newValue) =>
                                    setSelectedMemberType(newValue as MemberType)
                                }
                                inline
                            />
                            <SuggestSingleSelectFormik
                                id="member.memberId"
                                label="Existing Member"
                                placeholderText="Select An Existing Member"
                                isLoading={memberListFetching}
                                values={memberList}
                                selectedItem={memberList?.find(
                                    ({ memberId }) => memberId === formik.values.member?.memberId,
                                )}
                                onChange={(newValue) => {
                                    void formik.setFieldValue('member', newValue);
                                }}
                            />
                            <SuggestSingleSelectFormik
                                id="supplier_npi"
                                label="Supplier NPI"
                                isLoading={supplierListFetching}
                                values={supplierList}
                                required
                                selectedItem={supplierList?.find(
                                    ({ npi }) => npi === formik.values.supplier_npi,
                                )}
                                onChange={(newValue) => {
                                    void formik.setFieldValue('supplier_npi', newValue?.npi);
                                }}
                            />
                            <SuggestSingleSelectFormik
                                id="place_of_service"
                                label="Place of Service"
                                required
                                values={placeOfServiceOptions}
                                isLoading={placeOfServiceOptionsFetching}
                                selectedItem={placeOfServiceOptions?.find(
                                    ({ code }) => code === formik.values.place_of_service,
                                )}
                                onChange={(newValue) => {
                                    void formik.setFieldValue('place_of_service', newValue?.code);
                                }}
                            />
                            <DateInputFormik
                                id="claim_date_of_service"
                                label="Claim Date Of Service"
                                required
                            />
                            <DateInputFormik
                                id="adjudication_date"
                                label="Adjudication Data"
                                required
                            />
                            <NumberInputFormik
                                id="drug_days_supply"
                                label="Drug Days Supply"
                                required
                            />
                            <NumberInputFormik id="drug_quantity" label="Drug Quantity" required />
                            <DollarInputFormik
                                id="drug_ingredient_cost"
                                label="Drug Ingredient Cost"
                                required
                            />
                            <DollarInputFormik id="pt_deductible" label="Patient Deductible" />
                            <DollarInputFormik id="pt_coinsurance" label="Patient Coinsurance" />
                            <DollarInputFormik id="pt_copay" label="Patient Copay" />
                            <DollarInputFormik id="pt_pay_other" label="Patient Pay Other" />
                            <FormSectionWithRows
                                sectionTitle="Claim Details"
                                width="100%"
                                rowOneContent={
                                    <>
                                        <RadioButtonGroupFormik
                                            name="claim_status"
                                            label="Claim Status"
                                            options={ClaimStatuses}
                                            onChange={(newValue) => {
                                                void formik.setFieldValue('claim_status', newValue);
                                            }}
                                            required
                                        />
                                        <div style={{ width: '50%' }}>
                                            <NumberInputFormik
                                                id="sequence_number"
                                                label="Sequence Number"
                                                required
                                            />
                                            <TextInputFormik
                                                id="drug_prescription_number"
                                                label="Drug Prescription Number"
                                                required
                                            />
                                        </div>
                                    </>
                                }
                            />
                        </>
                    }
                    formRightContent={
                        <>
                            <div>Generated Claim:</div>
                            {formik.isSubmitting ? (
                                <Spinner intent={Spinner.Intent.NONE} />
                            ) : (
                                <pre>
                                    {generatedClaim && JSON.stringify(generatedClaim, null, 2)}
                                </pre>
                            )}
                        </>
                    }
                />
            )}
        </Formik>
    );
};
